import { Component, EventEmitter, Input, Output } from '@angular/core'

import { IGradeTier, ITier } from '../../data/interfaces/algorithm-config.interface'

/**
 * Editor genérico de tramos.
 *
 * Existe para no escribir siete formularios casi idénticos: lo reusan empleados, concentración de
 * clientes, concentración de proveedores, margen, tabla de tasas, plazos y grades.
 *
 * Su responsabilidad no negociable es **el orden**. Los tramos se evalúan first-match, así que uno
 * desordenado no rompe nada visiblemente: simplemente hace que los tramos de abajo no se alcancen
 * nunca, y las ofertas salen mal sin que nadie vea un error. Por eso el componente reordena al
 * emitir y avisa cuando lo hace.
 */
@Component({
  selector: 'app-config-tier-editor',
  templateUrl: './config-tier-editor.component.html',
  styleUrls: ['./config-tier-editor.component.scss'],
})
export class ConfigTierEditorComponent {
  @Input() label = ''
  @Input() hint = ''
  /** Qué se compara contra el umbral: 'Score', 'Empleados', 'Concentración', 'Margen'. */
  @Input() thresholdLabel = 'Umbral'
  @Input() thresholdUnit = ''
  @Input() thresholdStep = 1
  /** Qué resulta: 'Factor', 'Tasa', 'Meses'. */
  @Input() valueLabel = 'Valor'
  @Input() valueUnit = ''
  @Input() valueStep = 0.01
  @Input() valueMin: number | null = null
  @Input() valueMax: number | null = null
  /** Los grades resuelven a una letra, no a un número. */
  @Input() mode: 'numeric' | 'grade' = 'numeric'
  @Input() readonly = false

  @Input() tiers: Array<ITier | IGradeTier> = []
  //! El Output emite `any[]` a propósito. El componente es genérico sobre dos formas de tramo
  //! (`{min,value}` y `{min,grade}`), así que con `Array<ITier|IGradeTier>` el binding bidireccional
  //! `[(tiers)]="params.rateTable.tiers"` no compila: TypeScript no puede estrechar la unión al
  //! escribir de vuelta en un `ITier[]`. La entrada SÍ queda tipada, que es donde importa; la salida
  //! siempre devuelve el mismo arreglo que recibió, solo reordenado.
  @Output() tiersChange = new EventEmitter<any>()

  //* El `?` vive en el header del editor y no flotando sobre él: absolutamente posicionado se
  //* encimaba con «Agregar tramo» y, con cuatro editores en fila, quedaba a la deriva.
  @Output() helpRequested = new EventEmitter<Event>()

  /** Se muestra cuando el usuario dejó los tramos desordenados y el componente los reacomodó. */
  reordered = false

  get numericTiers(): ITier[] {
    return this.tiers as ITier[]
  }

  get gradeTiers(): IGradeTier[] {
    return this.tiers as IGradeTier[]
  }

  add(): void {
    const menor = this.tiers.length ? Math.min(...this.tiers.map((t) => t.min)) : 100
    const nuevo: any = this.mode === 'grade' ? { min: Math.max(0, menor - 50), grade: '' } : { min: Math.max(0, menor - 10), value: 1 }
    this.emit([...this.tiers, nuevo])
  }

  removeAt(i: number): void {
    this.emit(this.tiers.filter((_, idx) => idx !== i))
  }

  onChange(): void {
    this.emit([...this.tiers])
  }

  //* Reordenar aquí y no solo validar en el servidor: el Admin ve el efecto de inmediato en el
  //* orden de las filas, en vez de descubrir al activar que su configuración no era válida.
  private emit(tiers: Array<ITier | IGradeTier>): void {
    const antes = tiers.map((t) => t.min).join(',')
    const ordenados = [...tiers].sort((a, b) => b.min - a.min)
    this.reordered = ordenados.map((t) => t.min).join(',') !== antes
    this.tiers = ordenados
    this.tiersChange.emit(ordenados)
  }

  /** Umbrales repetidos: el segundo es inalcanzable. */
  get duplicados(): number[] {
    const vistos = new Set<number>()
    const dup = new Set<number>()
    for (const t of this.tiers) {
      if (vistos.has(t.min)) dup.add(t.min)
      vistos.add(t.min)
    }
    return [...dup]
  }
}
